import React, { useState } from "react";
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
  Link,
} from "react-router-dom";
import { useBuilding } from "../../context/BuildingContext";
import PageHeader from "../../components/common/PageHeader";

// Import report components
import FinancialReports from "./FinancialReports";
import BalanceSheetReport from "./BalanceSheetReport";
import CashFlowReport from "./CashFlowReport";
import AgedDebtorsReport from "./AgedDebtorsReport";
import ReserveFundReport from "./ReserveFundReport";
import BudgetVsActualReport from "./BudgetVsActualReport";
import AnnualAccountsReport from "./AnnualAccountsReport";
import ServiceChargeReports from "./ServiceChargeReports";
import UnitReports from "./UnitReports";
import MaintenanceReports from "./MaintenanceReports";
import CustomReport from "./CustomReport";
import TrialBalanceReport from "./TrialBalanceReport";
import ShowTransactionsReport from "./ShowTransactionsReport";
import BankReconciliationReport from "./BankReconciliationReport";
import AgedCreditorsReport from "./AgedCreditorsReport";
import UnitStatementReport from "./UnitStatementReport";
import SpendingCategoriesReport from "./SpendingCategoriesReport";
import CapitalAssetsReport from "./CapitalAssetsReport";
import AuditTrailReport from "./AuditTrailReport";
import ManagementPackReport from "./ManagementPackReport";

type ReportType = {
  id: string;
  name: string;
  description: string;
  path: string;
  icon: React.ReactNode;
};

const ReportsHub: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { currentBuilding } = useBuilding();
  const [reportPeriod, setReportPeriod] = useState("currentYear");

  // Define available reports
  const reportTypes: ReportType[] = [
    {
      id: "profit-loss",
      name: "Profit & Loss",
      description: "Income, expenses, and net profit analysis",
      path: "/dashboard/reports/profit-loss",
      icon: (
        <svg
          className="h-8 w-8 text-primary-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
    },
    {
      id: "balance-sheet",
      name: "Balance Sheet",
      description: "Assets, liabilities, and equity position",
      path: "/dashboard/reports/balance-sheet",
      icon: (
        <svg
          className="h-8 w-8 text-indigo-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      id: "cash-flow",
      name: "Cash Flow",
      description: "Monthly cash inflows and outflows",
      path: "/dashboard/reports/cash-flow",
      icon: (
        <svg
          className="h-8 w-8 text-green-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
          />
        </svg>
      ),
    },
    {
      id: "aged-debtors",
      name: "Aged Debtors",
      description: "Outstanding balances by aging bucket",
      path: "/dashboard/reports/aged-debtors",
      icon: (
        <svg
          className="h-8 w-8 text-red-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      id: "reserve-fund",
      name: "Reserve Fund",
      description: "Reserve fund balance and movements",
      path: "/dashboard/reports/reserve-fund",
      icon: (
        <svg
          className="h-8 w-8 text-orange-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v13m0-13V6a2 2 0 10-4 0v2H4a2 2 0 00-2 2v7a2 2 0 002 2h16a2 2 0 002-2v-7a2 2 0 00-2-2h-4V6a2 2 0 10-4 0z"
          />
        </svg>
      ),
    },
    {
      id: "budget-vs-actual",
      name: "Budget vs Actual",
      description: "Budgeted vs actual expenditure",
      path: "/dashboard/reports/budget-vs-actual",
      icon: (
        <svg
          className="h-8 w-8 text-cyan-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 8v8m-4-5v5m-4-2v2m-2 0a2 2 0 012-2h12a2 2 0 012 2M4 20h16a2 2 0 002-2V6a2 2 0 00-2-2H4a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      id: "annual-accounts",
      name: "Annual Accounts",
      description: "Formal annual accounts and certified statements",
      path: "/dashboard/reports/annual-accounts",
      icon: (
        <svg
          className="h-8 w-8 text-purple-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
    },
    {
      id: "service-charge",
      name: "Service Charge",
      description: "Service charge collection and analysis",
      path: "/dashboard/reports/service-charge",
      icon: (
        <svg
          className="h-8 w-8 text-green-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
    },
    {
      id: "units",
      name: "Unit Reports",
      description: "Occupancy and unit allocation",
      path: "/dashboard/reports/units",
      icon: (
        <svg
          className="h-8 w-8 text-purple-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      ),
    },
    {
      id: "maintenance",
      name: "Maintenance",
      description: "Maintenance history and upcoming work",
      path: "/dashboard/reports/maintenance",
      icon: (
        <svg
          className="h-8 w-8 text-yellow-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
    },
    {
      id: "custom",
      name: "Custom Reports",
      description: "Create and save custom reports",
      path: "/dashboard/reports/custom",
      icon: (
        <svg
          className="h-8 w-8 text-red-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
          />
        </svg>
      ),
    },
    ...(
      [
        [
          "trial-balance",
          "Trial Balance",
          "Debit/credit balances for every nominal at period end",
        ],
        [
          "show-transactions",
          "Show Transactions",
          "Nominal ledger postings for a chosen category",
        ],
        [
          "bank-reconciliation",
          "Bank Reconciliation",
          "Book vs bank balance with uncleared items",
        ],
        [
          "aged-creditors",
          "Aged Creditors",
          "Unpaid supplier bills bucketed by age",
        ],
        [
          "unit-statement",
          "Unit Statement",
          "Per-unit demands, receipts and balance",
        ],
        [
          "spending-categories",
          "Spending Categories",
          "Total spend per expense category",
        ],
        [
          "capital-assets",
          "Capital Assets",
          "Fixed assets register with cost and depreciation",
        ],
        [
          "audit-trail",
          "Audit Trail",
          "Chronological log of every financial mutation",
        ],
        [
          "management-pack",
          "Management Pack",
          "Configurable bundle of reports for board meetings",
        ],
      ] as const
    ).map(([id, name, description]) => ({
      id,
      name,
      description,
      path: `/dashboard/reports/${id}`,
      icon: (
        <svg
          className="h-8 w-8 text-slate-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 17v-2a4 4 0 014-4h4M9 17H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v6"
          />
        </svg>
      ),
    })),
  ];

  // Redirect to profit & loss report if on base reports URL
  React.useEffect(() => {
    if (
      pathname === "/dashboard/reports" ||
      pathname === "/dashboard/reports/"
    ) {
      navigate("/dashboard/reports/profit-loss", { replace: true });
    }
  }, [pathname, navigate]);

  return (
    <div className="">
      <PageHeader
        title="Reports"
        description={`View and generate reports for ${
          currentBuilding?.name || "your building"
        }`}
        actions={
          <div className="flex space-x-2">
            <select
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              value={reportPeriod}
              onChange={(e) => setReportPeriod(e.target.value)}
            >
              <option value="currentMonth">Current Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="currentQuarter">Current Quarter</option>
              <option value="lastQuarter">Last Quarter</option>
              <option value="currentYear">Current Year</option>
              <option value="lastYear">Last Year</option>
              <option value="custom">Custom Range</option>
            </select>
            <button
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={() => {
                /* In a real app, trigger export or print functionality */
              }}
            >
              Export Reports
            </button>
          </div>
        }
      />

      {/* Reports Navigation */}
      <div className="mt-6 mb-6">
        <label
          htmlFor="report-select"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Report Type
        </label>
        <select
          id="report-select"
          className="block w-full sm:w-72 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md shadow-sm"
          value={reportTypes.find((r) => pathname.includes(r.path))?.path ?? ""}
          onChange={(e) => navigate(e.target.value)}
          aria-label="Select report type"
        >
          {reportTypes.map((report) => (
            <option key={report.id} value={report.path}>
              {report.name}
            </option>
          ))}
        </select>
      </div>

      {/* Report Content */}
      <div>
        <Routes>
          {/* Financial Reports */}
          <Route
            path="profit-loss/*"
            element={<FinancialReports period={reportPeriod} />}
          />
          <Route
            path="balance-sheet/*"
            element={<BalanceSheetReport period={reportPeriod} />}
          />
          <Route
            path="cash-flow/*"
            element={<CashFlowReport period={reportPeriod} />}
          />
          <Route
            path="aged-debtors/*"
            element={<AgedDebtorsReport period={reportPeriod} />}
          />
          <Route
            path="reserve-fund/*"
            element={<ReserveFundReport period={reportPeriod} />}
          />
          <Route
            path="budget-vs-actual/*"
            element={<BudgetVsActualReport period={reportPeriod} />}
          />
          <Route
            path="annual-accounts/*"
            element={<AnnualAccountsReport period={reportPeriod} />}
          />

          {/* Other Report Categories */}
          <Route
            path="service-charge/*"
            element={<ServiceChargeReports period={reportPeriod} />}
          />
          <Route
            path="units/*"
            element={<UnitReports period={reportPeriod} />}
          />
          <Route
            path="maintenance/*"
            element={<MaintenanceReports period={reportPeriod} />}
          />
          <Route
            path="custom/*"
            element={<CustomReport period={reportPeriod} />}
          />
          <Route
            path="trial-balance/*"
            element={<TrialBalanceReport period={reportPeriod} />}
          />
          <Route
            path="show-transactions/*"
            element={<ShowTransactionsReport period={reportPeriod} />}
          />
          <Route
            path="bank-reconciliation/*"
            element={<BankReconciliationReport period={reportPeriod} />}
          />
          <Route
            path="aged-creditors/*"
            element={<AgedCreditorsReport period={reportPeriod} />}
          />
          <Route
            path="unit-statement/*"
            element={<UnitStatementReport period={reportPeriod} />}
          />
          <Route
            path="spending-categories/*"
            element={<SpendingCategoriesReport period={reportPeriod} />}
          />
          <Route
            path="capital-assets/*"
            element={<CapitalAssetsReport period={reportPeriod} />}
          />
          <Route
            path="audit-trail/*"
            element={<AuditTrailReport period={reportPeriod} />}
          />
          <Route
            path="management-pack/*"
            element={<ManagementPackReport period={reportPeriod} />}
          />
        </Routes>
      </div>

      {/* Report Dashboard (shown when no specific report type is selected) */}
      {pathname === "/dashboard/reports" && (
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {reportTypes.map((report) => (
            <Link
              key={report.id}
              to={report.path}
              className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow duration-300"
            >
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">{report.icon}</div>
                  <div className="ml-5">
                    <h3 className="text-lg font-medium text-gray-900">
                      {report.name}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {report.description}
                    </p>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportsHub;
